import React, { useState } from "react";
import {
  Button,
  Container,
  Form,
  Modal,
  Badge,
  Row,
  Col,
  Card,
  Table,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import { faEye } from "@fortawesome/free-solid-svg-icons";

import { getTabHistory } from "src/Services/api";
import useQuery from "src/Hooks/useQuery";
import JamlyfTable from "src/Components/Table";
import LoadingOverlay from "src/Components/LoadingOverlay";
import { formatCurrency, formatDateTime } from "src/Utils/formatters"
import { isSearchableString } from "src/Utils/queryHelpers";
import SearchBox from "src/Components/SearchBox";
import Notification, { useNotification } from "src/Components/Notification";
import "./style.css";

const TAB_HEADER_TABLE_MAPPER = {
  user_name: "Raver",
  amount_without_charges: "Sub Total",
  charges: "Charges",
  amount: "Total",
  total_orders: "Number of Items",
  created_at: "Opened At",
  status: "Status",
};

const STATE_BADGE_VARIANTS = {
  awaiting_order: "info",
  opened: "success",
  cancelled: "danger",
  awaiting_approval: "warning",
  approved_and_closed: "secondary",
};

export const tabStateMapping = {
  null: "awaiting_order",
  1: "opened",
  2: "cancelled",
  3: "awaiting_approval",
  4: "approved_and_closed",
};

const getTabQueryParams = (id, searchTerm, pageNumber, filters) => {
  const queryParams = { id };
  
  if (!!searchTerm && isSearchableString(searchTerm)) {
    queryParams["search"] = searchTerm;
  }
  
  if (!!pageNumber) {
    queryParams["page"] = pageNumber;
  }
  
  if (filters.status && filters.status !== "all") {
    queryParams["status"] = filters.status;
  }
  
  if (filters.dateFrom) {
    queryParams["date_from"] = filters.dateFrom;
  }
  
  if (filters.dateTo) {
    queryParams["date_to"] = filters.dateTo;
  }
  
  if (filters.sortBy) {
    queryParams["sort_by"] = filters.sortBy;
  }
  
  return queryParams;
};

const StatusBadge = ({ state }) => {
  const tabState = tabStateMapping[state];
  const variant = STATE_BADGE_VARIANTS[tabState] || "secondary";
  const label = tabState.replace(/_/g, " ").toUpperCase();
  return <Badge bg={variant}>{label}</Badge>;
};

const TabHistoryPage = () => {
  const [searchStr, setSearchStr] = useState("");
  const [pageNum, setPageNum] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    dateFrom: "",
    dateTo: "",
    sortBy: "-created_at",
  });
  
  const { id } = useParams();
  const { notifyError, onClose, notification } = useNotification();

  const queryParams = getTabQueryParams(id, searchStr, pageNum, filters);
  const {
    loading,
    response = {},
    refetch: refetchTabs,
  } = useQuery(getTabHistory, queryParams);
  
  const { data = {} } = response;
  const {
    objects: paginatedData = [],
    total_objects,
    total_pages,
    summary = {},
    currency,
  } = data;

  const formattedData = paginatedData.map((tab) => ({
    ...tab,
    user_name: tab.user ? `${tab.user.first_name} ${tab.user.last_name}` : "Guest",
    amount: formatCurrency(tab.amount, currency),
    amount_without_charges: formatCurrency(tab.amount_without_charges, currency),
    charges: formatCurrency(tab.charges, currency),
    created_at: formatDateTime(tab.created_at),
    status: <StatusBadge state={tab.state} />,
    total_orders: tab.orders.length || 0,
  }));

  const handleTabView = (tab) => {
    const originalTab = paginatedData.find((t) => t.id === tab.id);
    setSelectedTab(originalTab);
    setShowDetailsModal(true);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setPageNum(1);
  };

  const handleResetFilters = () => {
    setFilters({
      status: "all",
      dateFrom: "",
      dateTo: "",
      sortBy: "-created_at",
    });
    setSearchStr("");
    setPageNum(1);
  };

  // Calculate total items from orders
  const calculateTotalItems = (orders) => {
    return orders.reduce((total, order) => total + (order.quantity || 0), 0);
  };

  return (
    <Container className="mt-4">
      <Notification id="tab-history" onClose={onClose} {...notification} />
      
      {/* Header */}
      <div style={{ paddingBottom: 20, paddingTop: 20 }}>
        <div className="d-inline-flex align-items-center">
          <h5 className="mb-0">Tab History</h5>
          <span className="ps-2 fs-6 fw-lighter">
            ({total_objects} {total_objects !== 1 ? "tabs" : "tab"})
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center summary-card">
            <Card.Body>
              <Card.Title className="text-muted fs-6 text-uppercase">Total Revenue</Card.Title>
              <Card.Text className="fs-4 fw-bold text-success">
                {formatCurrency(summary.total_revenue || 0, currency)}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center summary-card">
            <Card.Body>
              <Card.Title className="text-muted fs-6 text-uppercase">Open Tabs</Card.Title>
              <Card.Text className="fs-4 fw-bold text-primary">
                {summary.open_tabs || 0}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center summary-card">
            <Card.Body>
              <Card.Title className="text-muted fs-6 text-uppercase">Closed Tabs</Card.Title>
              <Card.Text className="fs-4 fw-bold text-secondary">
                {summary.closed_tabs || 0}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-3 gy-2">
        <Col md={4}>
          <SearchBox
            id="formTabSearch"
            placeholder="Search by customer name, email or amount"
            defaultValue={searchStr}
            onChange={setSearchStr}
            isLoading={loading && isSearchableString(searchStr)}
            onReset={() => setSearchStr("")}
            disabled={!searchStr && !paginatedData?.length}
          />
        </Col>
        <Col md={2}>
          <Form.Select
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            className="historyFormInput"
          >
            <option value="all">All Status</option>
            {Object.keys(tabStateMapping).map((key) => (
              <option key={key} value={key}>
                {tabStateMapping[key].replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
            placeholder="From Date"
            className="historyFormInput"
          />
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange("dateTo", e.target.value)}
            placeholder="To Date"
            className="historyFormInput"
          />
        </Col>
        <Col md={2}>
          <Button
            variant="outline-secondary"
            onClick={handleResetFilters}
            className="w-100"
          >
            Reset Filters
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <LoadingOverlay show={loading} />
      <JamlyfTable
        data={formattedData}
        headers={Object.values(TAB_HEADER_TABLE_MAPPER)}
        headerMapper={TAB_HEADER_TABLE_MAPPER}
        showActions={true}
        onEditActionClick={handleTabView}
        onPageChange={setPageNum}
        totalPages={total_pages}
        editIcon={faEye}
      />

      {/* Tab Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="xl"
        centered
        className="tab-details-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="w-100">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">Tab Details</h5>
                <small className="text-white-50 fs-6">
                  Tab ID: {selectedTab?.slug}
                </small>
              </div>
              <StatusBadge state={selectedTab?.state} />
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-3">
          {selectedTab && (
            <>
              {/* Customer Information Card */}
              <Card className="mb-3 border-0 shadow-sm">
                <Card.Body>
                  <h6 className="text-muted text-uppercase mb-3" style={{ fontSize: "0.85rem", letterSpacing: "0.5px" }}>
                    Customer Information
                  </h6>
                  <Row>
                    <Col md={6} className="mb-3">
                      <div className="info-item">
                        <small className="text-muted d-block mb-1">Name</small>
                        <strong>
                          {selectedTab.user 
                            ? `${selectedTab.user.first_name} ${selectedTab.user.last_name}`
                            : "Guest"}
                        </strong>
                      </div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <div className="info-item">
                        <small className="text-muted d-block mb-1">Email</small>
                        <strong>{selectedTab.user?.email || "N/A"}</strong>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="info-item">
                        <small className="text-muted d-block mb-1">Phone</small>
                        <strong>{selectedTab.user?.phone || "N/A"}</strong>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="info-item">
                        <small className="text-muted d-block mb-1">Tab Opened</small>
                        <strong>{formatDateTime(selectedTab.created_at)}</strong>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Payment Summary Card */}
              <Card className="mb-3 border-0 shadow-sm">
                <Card.Body>
                  <h6 className="text-muted text-uppercase mb-3" style={{ fontSize: "0.85rem", letterSpacing: "0.5px" }}>
                    Payment Summary
                  </h6>
                  <Row>
                    <Col md={4} className="mb-3">
                      <div className="info-item">
                        <small className="text-muted d-block mb-1">Subtotal</small>
                        <strong className="fs-5">
                          {formatCurrency(selectedTab.amount_without_charges, currency)}
                        </strong>
                      </div>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div className="info-item">
                        <small className="text-muted d-block mb-1">Charges/Tax</small>
                        <strong className="fs-5 text-info">
                          {formatCurrency(selectedTab.charges, currency)}
                        </strong>
                      </div>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div className="info-item">
                        <small className="text-muted d-block mb-1">Total Amount</small>
                        <strong className="fs-4 text-success">
                          {formatCurrency(selectedTab.amount, currency)}
                        </strong>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Orders Card */}
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="text-muted text-uppercase mb-0" style={{ fontSize: "0.85rem", letterSpacing: "0.5px" }}>
                      Ordered Items
                    </h6>
                    <Badge bg="secondary" className="px-3">
                      {selectedTab.orders.length} {selectedTab.orders.length === 1 ? "Item" : "Items"} 
                      {" "}({calculateTotalItems(selectedTab.orders)} total quantity)
                    </Badge>
                  </div>
                  
                  {selectedTab.orders.length > 0 ? (
                    <div className="table-responsive">
                      <Table hover className="orders-table mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "50%" }}>Item</th>
                            <th className="text-center" style={{ width: "15%" }}>Quantity</th>
                            <th className="text-end" style={{ width: "20%" }}>Unit Price</th>
                            <th className="text-end" style={{ width: "15%" }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTab.orders
                            .filter(order => !order.deleted_at)
                            .map((order, index) => {
                              const itemTotal = parseFloat(order.menu_item.price) * order.quantity;
                              return (
                                <tr key={order.id || index}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      {order.menu_item.image && (
                                        <img
                                          src={order.menu_item.image}
                                          alt={order.menu_item.name}
                                          className="me-3 rounded"
                                          style={{
                                            width: "50px",
                                            height: "50px",
                                            objectFit: "cover",
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                          }}
                                        />
                                      )}
                                      <div>
                                        <div className="fw-medium">
                                          {order.menu_item.name}
                                        </div>
                                        {order.menu_item.description && (
                                          <small className="text-muted">
                                            {order.menu_item.description}
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-center align-middle">
                                    <Badge bg="light" text="dark" className="px-3">
                                      {order.quantity}
                                    </Badge>
                                  </td>
                                  <td className="text-end align-middle">
                                    {formatCurrency(order.menu_item.price, currency)}
                                  </td>
                                  <td className="text-end align-middle fw-medium">
                                    {formatCurrency(itemTotal, currency)}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted">
                      <i className="bi bi-basket" style={{ fontSize: "2rem" }}></i>
                      <p className="mb-0 mt-2">No items ordered</p>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Reason for cancellation if cancelled */}
              {selectedTab.state === 2 && selectedTab.reason_for_cancel && (
                <Card className="mt-3 border-danger">
                  <Card.Body className="bg-danger bg-opacity-10">
                    <h6 className="text-danger text-uppercase mb-2" style={{ fontSize: "0.85rem" }}>
                      Cancellation Reason
                    </h6>
                    <p className="mb-0">{selectedTab.reason_for_cancel}</p>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 bg-light">
          <Button
            variant="secondary"
            onClick={() => setShowDetailsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TabHistoryPage;